import { createContext, useState } from "react";
import { useDispatch } from "react-redux";
import { isEmpty } from "lodash";

import {
  createEmployeeService,
  createEmployerService,
  deleteEmployeeService,
  deleteEmployerService,
  editEmployeeService,
  getEmployeesService,
  getEmployersService,
  getPersonnelsEmployerService,
  getUsersService,
} from "./../../../services/userServices";
import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "./../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";

export const employerAdminContext = createContext({
  employer: {},
  setEmployer: () => {},
  users: [],
  setUsers: () => {},
  employers: [],
  setEmployers: () => {},
  userSearch: "",
  setUserSearch: () => {},
  search: "",
  setSearch: () => {},
  id: "",
  setId: () => {},
  name: "",
  setName: () => {},
  code: "",
  setCode: () => {},
  post: "",
  setPost: () => {},
  user: {},
  setUser: () => {},
  employees: [],
  setEmployees: () => {},
  primaryCharge: "",
  setPrimaryCharge: () => {},
  totalCount: null,
  setTotalCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  limit: null,
  setLimit: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  isShowPersonnelModal: null,
  setIsShowPersonnelModal: () => {},
  isShowEmployeeModal: null,
  setIsShowEmployeeModal: () => {},
  isUserLoading: null,
  setIsUserLoading: () => {},
  handleShowEmployeeModal: () => {},
  handleCloseEmployeeModal: () => {},
  handleShowModal: () => {},
  handleCloseModal: () => {},
  handleShowPersonnelModal: () => {},
  handleClosePersonnelModal: () => {},
  handleGetEmployerList: () => {},
  handleGetEmployeeList: () => {},
  handleCreateEmployer: () => {},
  handleEditEmployer: () => {},
  handleDeleteEmployer: () => {},
  handleAddEmployee: () => {},
  handleRemoveEmployee: () => {},
  handleGetEmployees: () => {},
  handleDeleteEmployee: () => {},
  handleCreateEmployee: () => {},
  handleEditEmployee: () => {},
  handleResetState: () => {},
  handleGetUsers: () => {},
});

const EmployerAdminContext = ({ children }) => {
  // Redux Dispatch
  const dispatch = useDispatch();

  const [employer, setEmployer] = useState({});
  const [users, setUsers] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [search, setSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [post, setPost] = useState("");
  const [user, setUser] = useState({});
  const [employees, setEmployees] = useState([]);
  const [primaryCharge, setPrimaryCharge] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [limit, setLimit] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);
  const [isShowPersonnelModal, setIsShowPersonnelModal] = useState(false);
  const [isShowEmployeeModal, setIsShowEmployeeModal] = useState(false);
  const [isUserLoading, setIsUserLoading] = useState(false);

  const handleResetState = () => {
    setUsers([]);
    setId("");
    setName("");
    setCode("");
    setPost("");
    setUser({});
    setUserSearch("");
    setSearch("");
    setEmployees([]);
    setEmployer({});
    setPrimaryCharge("");
    setTotalCount(0);
    setPageNumber(1);
    setLimit(100);
    setIsUserLoading(false);
  };

  // Create Employer Modal Operations
  const handleShowModal = (employerValue) => {
    setIsShowModal(true);
    if (employerValue) {
      setId(employerValue.id);
      setCode(employerValue.organization_code);
      setName(employerValue.name);
      setEmployees([]);
      setPrimaryCharge(employerValue.primary_charge);
    }
  };
  const handleCloseModal = () => {
    setIsShowModal(false);
    handleResetState();
  };

  // Create Personnel Modal Operations
  const handleShowPersonnelModal = (employeeValue) => {
    setIsShowPersonnelModal(true);
    if (employeeValue) setEmployees(employeeValue);
  };
  const handleClosePersonnelModal = () => {
    setIsShowPersonnelModal(false);
    handleResetState();
  };

  // Create Employee Modal Operations
  const handleShowEmployeeModal = (value) => {
    setIsShowEmployeeModal(true);
    if (value) {
      setId(value.id);
      setCode(value.employee_code);
      setPost(value.post);
      setUser(value.user);
    }
  };
  const handleCloseEmployeeModal = () => {
    setIsShowEmployeeModal(false);
    setId("");
    setCode("");
    setPost("");
    setUserSearch("");
    setUser({});
  };

  // Handle Get Employer List Method
  const handleGetEmployerList = async (event) => {
    event?.preventDefault();

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getEmployersService();
      if (status === 200) {
        setEmployers(data.list);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Employee List Method
  const handleGetEmployeeList = async (_item) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getPersonnelsEmployerService({
        employer_id: _item.id,
      });
      if (status === 200) {
        if (isEmpty(data.list)) toastErrorMessage("پرسنلی برای نمایش یافت نشد");
        else handleShowPersonnelModal(data.list);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create Employer Method
  const handleCreateEmployer = async (event) => {
    event?.preventDefault();

    const objData = {
      name,
      code,
      employees,
      primary_charge: primaryCharge,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createEmployerService(objData);
      if (status === 200) {
        toastSuccessMessage("سازمان با موفقیت ایجاد شد");
        handleGetEmployerList();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Employer Method
  const handleEditEmployer = async (event) => {
    event?.preventDefault();

    const objData = {
      id,
      name,
      code,
      employees,
      primary_charge: primaryCharge,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createEmployerService(objData);
      if (status === 200) {
        toastSuccessMessage("آیتم با موفقیت ویرایش شد");
        handleGetEmployerList();
        handleCloseModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Employer Method
  const handleDeleteEmployer = async (emp_code) => {
    const objData = {
      code: emp_code,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteEmployerService(objData);
      if (status === 200) {
        toastSuccessMessage(`سازمان  ${emp_code} با موفقیت حذف شد`);
        handleGetEmployerList();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Get Users Method
  const handleGetUsers = async (event) => {
    event?.preventDefault();
    
    const isSearch = !isEmpty(event);

    const objData = {
      search: userSearch,
      offset: ((isSearch ? 1 : pageNumber) - 1) * limit,
      limit,
    };

    try {
      setIsUserLoading(true);
      const { status, data } = await getUsersService(objData);
      if (status === 200) {
        setUsers(data.results);
        setTotalCount(data.count);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      setIsUserLoading(false);
    }
  };

  // Handle Add Employee Method
  const handleAddEmployee = (event) => {
    event.preventDefault();

    const code = event.target.employeeCode.value;
    const post = event.target.employeePost.value;

    if (!code || !post) return toastErrorMessage("اطلاعات وارد شده نادرست");

    setEmployees((prevEmployees) => [
      ...prevEmployees,
      {
        code,
        post,
      },
    ]);
    event.target.reset();
  };

  // Handle Remove Employee Method
  const handleRemoveEmployee = (employeeCode) => {
    setEmployees((prevEmployees) =>
      prevEmployees.filter((pE) => pE.code !== employeeCode)
    );
  };

  // Handle Get Employees Method
  const handleGetEmployees = async (_item) => {
    const objData = {
      search,
      employer_id: _item.id,
      offset: (pageNumber - 1) * limit,
      limit,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getEmployeesService(objData);
      if (status === 200) {
        setEmployer(_item);
        setEmployees(data.results);
        setTotalCount(data.count);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Delete Employee Method
  const handleDeleteEmployee = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status } = await deleteEmployeeService(id);
      if (status === 200) {
        toastSuccessMessage("کارمند با موفقیت حذف شد");
        handleGetEmployees(employer);
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Create Employee Method
  const handleCreateEmployee = async () => {
    const objData = {
      post,
      employee_code: code,
      employer: employer.id,
      user: user.pk,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await createEmployeeService(objData);
      if (status === 201) {
        toastSuccessMessage("کارمند با موفقیت حذف شد");
        handleGetEmployees(employer);
        handleCloseEmployeeModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Handle Edit Employee Method
  const handleEditEmployee = async () => {
    const objData = {
      post,
      employee_code: code,
      employer: employer.id,
      user_id: user.pk,
      id,
    };

    try {
      dispatch(setLoadingTrueAction());
      const { status } = await editEmployeeService(objData);
      if (status === 201) {
        toastSuccessMessage("کارمند با موفقیت ویرایش شد");
        handleGetEmployees(employer);
        handleCloseEmployeeModal();
      }
    } catch ({ response }) {
      if (response?.status) {
        toastErrorMessage();
      }
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <employerAdminContext.Provider
      value={{
        users,
        setUsers,
        employer,
        setEmployer,
        employers,
        setEmployers,
        userSearch,
        setUserSearch,
        search,
        setSearch,
        id,
        setId,
        name,
        setName,
        code,
        setCode,
        post,
        setPost,
        user,
        setUser,
        employees,
        setEmployees,
        primaryCharge,
        setPrimaryCharge,
        totalCount,
        setTotalCount,
        pageNumber,
        setPageNumber,
        limit,
        setLimit,
        isShowModal,
        setIsShowModal,
        isShowPersonnelModal,
        setIsShowPersonnelModal,
        isShowEmployeeModal,
        setIsShowEmployeeModal,
        isUserLoading,
        setIsUserLoading,
        handleShowEmployeeModal,
        handleCloseEmployeeModal,
        handleShowModal,
        handleCloseModal,
        handleShowPersonnelModal,
        handleClosePersonnelModal,
        handleGetEmployerList,
        handleGetEmployeeList,
        handleCreateEmployer,
        handleEditEmployer,
        handleDeleteEmployer,
        handleAddEmployee,
        handleRemoveEmployee,
        handleGetEmployees,
        handleDeleteEmployee,
        handleCreateEmployee,
        handleEditEmployee,
        handleResetState,
        handleGetUsers,
      }}
    >
      {children}
    </employerAdminContext.Provider>
  );
};

export default EmployerAdminContext;
